import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../../../components/button/Button";
import { BUTTON_TYPE } from "../../../components/button/constants";
import { HEADING } from "../../../components/heading/constants/constants";
import Heading from "../../../components/heading/Heading";
import HorizontalSeparator from "../../../components/separator/horizontalSeparator/HorizontalSeparator";
import Loadable from "../../../components/loadable/Loadable";
import { ENDPOINT_RESULT_TYPE, PAGE_STATE } from "../../constants/constants";
import PreLoginLayout from "../components/preloginlayout/PreLoginLayout";
import { componentKey as forgotPasswordComponentKey, setEndpointResult, setResetPasswordFormFields } from "./ForgotPasswordSlice";
import { componentKey as authenticationComponentKey } from "../../authentication/AuthenticationSlice";
import { componentKey as appLoaderComponentKey } from "../../loaders/apploader/AppLoaderSlice";
import styles from './ForgotPassword.styles';
import { useDispatch, useSelector } from "react-redux";
import PasswordInput from "../../../components/input/passwordinput/PasswordInput";
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import { setPassword, validateToken } from "./ForgotPasswordSaga";
import { PAGE_PATH } from "../../dashboard/constants/constant";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useCallback } from "react";
import { PRE_LOGIN_LOADING_STATES, PRE_LOGIN_STATES_MESSAGE_MAP } from "../constants";
import useTxNavigate from "../../../../libs/txnavigate/TxNavigateHook";
import useGarbageCollector from "../../../../libs/garbagecollector/garbageCollectorHooks";
import General from "../../../../libs/utility/General";

export default function SetPassword() {

    useGarbageCollector(forgotPasswordComponentKey)

    const { loadingState, endpointResult, resetPasswordFormFields, validResetToken} = useSelector(state => state[forgotPasswordComponentKey]);
    const { isUserAuthenticated } = useSelector((state) => state[authenticationComponentKey]);
    const { pageLoadingState } = useSelector((state) => state[appLoaderComponentKey]);
    const { register, control, handleSubmit, watch, formState: {errors}, reset } = useForm();
    const dispatch = useDispatch();
    const navigate = useTxNavigate();
    const location = useLocation();
    const queryParams = parse(location.search);

    const { executeRecaptcha } = useGoogleReCaptcha();


    const handleReCaptchaVerify = useCallback(async (executionTag) => {

        if (executeRecaptcha) {
            return await executeRecaptcha(executionTag);
        }
        
    }, [executeRecaptcha]);

    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);


    useEffect(() => {
        if(pageLoadingState === PAGE_STATE.PAGE_READY){
            if(queryParams?.email !== "" && queryParams?.email !== undefined && queryParams?.token !== "" && queryParams?.token !== undefined){
                handleReCaptchaVerify("Validate_token").then(gToken =>{
                    const token = General.handleGRecaptchaToken(gToken)
                    if(token) {
                        dispatch(validateToken({email: queryParams.email, token: queryParams.token, GRecaptchaResponse: token}))
                    }
                }
                )
            }else{
                dispatch(setEndpointResult({showMessage: true, message: "You have accessed an invalid url", resulType: ENDPOINT_RESULT_TYPE.ERROR}))
            }
        }
    }, [pageLoadingState, handleReCaptchaVerify]);

    useEffect(() => {
        if(isUserAuthenticated){
            navigate(PAGE_PATH.DASHBOARD)
        }
    }, [isUserAuthenticated]);

    const password = watch('password');
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}$/;

    const validateConfirmPassword = (value) => {
        if (value !== password) {
        return "Passwords don't match";
        }
    };

    const handleInputChange = (e, onChangeValidationController, fieldType) => {
        const { name, value } = e.target;
        onChangeValidationController(value);
        let formFields = {};
        formFields = {
            ...resetPasswordFormFields,
            [name]: value,
        }
        dispatch(setResetPasswordFormFields(formFields));
    };

    const handleFormSubmit = (data) => {
        handleReCaptchaVerify("RESET_PASSWORD").then(gToken =>{
            const token = General.handleGRecaptchaToken(gToken)
            if(token) {
                dispatch(setPassword({...resetPasswordFormFields, GRecaptchaResponse: token}));
            }
        });  
    };


    return <>
        <PreLoginLayout>
            <Loadable
                currentState={loadingState.state}
                loadingStates={[PRE_LOGIN_LOADING_STATES.SET_PASSWORD_PAGE_LOADING]}
                messageMap={PRE_LOGIN_STATES_MESSAGE_MAP}
                isNewLoadable={true}
            >
                <div style={styles.passwordForm}>
                    <Heading type={HEADING.H1}>Reset Password</Heading>
                    <form onSubmit={handleSubmit(handleFormSubmit)}>

                        {
                            validResetToken && <>
                                <div style={styles.marginTop}>
                                    <Controller
                                        control={control}
                                        name="password"
                                        defaultValue={resetPasswordFormFields.password}
                                        rules={{ 
                                            required: "Password cannot be empty",
                                            pattern: {
                                                value: passwordRegex,
                                                message: "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character"
                                            }
                                        }}
                                        render={({ field : {value, onChange} }) => (
                                            <PasswordInput
                                                name="password" 
                                                type="password" 
                                                placeHolder='Password' 
                                                value={value} 
                                                onChangeCb={(e) => handleInputChange(e, onChange, "password")} 
                                            />
                                        )}
                                    />
                                    {errors.password && <span style={styles.errorMessage}>{errors.password.message}</span>}
                                    <div style={styles.marginTopDependentField}>                        
                                        <Controller
                                            control={control}
                                            name="confirmPassword"
                                            defaultValue={resetPasswordFormFields.confirmPassword}
                                            rules={{ 
                                                required: "Confirm password cannot be empty",
                                                validate: validateConfirmPassword,
                                            }}
                                            render={({ field : {value, onChange} }) => (
                                                <PasswordInput 
                                                    name="confirmPassword" 
                                                    type="password" 
                                                    placeHolder='Confirm password' 
                                                    value={value} 
                                                    onChangeCb={(e) => handleInputChange(e, onChange, "password")} 
                                                />
                                            )}
                                        />
                                        {errors.confirmPassword && <span style={styles.errorMessage}>{errors.confirmPassword.message}</span>}
                                    </div>    
                                </div>
                                <div style={styles.resetButtonDiv}>
                                    <Button type={BUTTON_TYPE.PRIMARY}>
                                        Reset password
                                    </Button>
                                </div>
                        </>
                        }
                        <div style={styles.errorMessageContainer}>
                            {
                                endpointResult.showMessage ? 
                                    <div style={{...styles.errorMessage, ...(endpointResult.resulType === ENDPOINT_RESULT_TYPE.SUCCESS) ? styles.messageSuccess: ""}}>
                                        {endpointResult.message}
                                    </div>
                                : ""
                            }    
                        </div>
                    </form>
                    <div style={styles.separatorDiv}><HorizontalSeparator /></div>
                    <div style={styles.signInOptionContainer}>
                        <span>Back to</span>
                        <div>
                            <Button type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER} style={{...styles.forgotPasswordLink, ...styles.signInLink}} onClickCb={() => {navigate('/signin')}}>
                                Sign In
                            </Button>
                        </div>
                    </div>
                </div>
            </Loadable>
        </PreLoginLayout>
    </>
}